import React from 'react';
import { Dialog, DialogContent } from '../ui/dialog';
import { CompanyIcon, fmtPrice } from '../CompanyCard';
import { buildOrderBook } from '../../mock';
import { X } from 'lucide-react';

export default function OrderBookModal({ company, open, onClose }) {
  const { bids, asks } = React.useMemo(() => (company ? buildOrderBook(company) : { bids: [], asks: [] }), [company]);
  if (!company) return null;
  const maxTotal = Math.max(...bids.map((b) => b.total), ...asks.map((a) => a.total));

  const Row = ({ row, side }) => {
    const pct = (row.total / maxTotal) * 100;
    const color = side === 'bid' ? 'var(--green)' : 'var(--red)';
    return (
      <div className="relative grid grid-cols-3 py-1.5 px-3 text-[12px] font-mono">
        <div
          className="absolute inset-y-0 right-0"
          style={{ width: `${pct}%`, background: side === 'bid' ? 'rgba(22,199,132,0.10)' : 'rgba(239,68,68,0.10)' }}
        />
        <span className="relative z-10" style={{ color }}>{fmtPrice(row.price)}</span>
        <span className="relative z-10 text-right text-[var(--text)]">{row.size.toLocaleString()}</span>
        <span className="relative z-10 text-right text-[var(--muted)]">{row.total.toLocaleString()}</span>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[var(--panel)] border-[var(--border)] text-[var(--text)] max-w-2xl p-0 gap-0 [&>button]:hidden">
        <div className="flex items-center justify-between p-5 border-b border-[var(--border)]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#1c1710] border border-[#3a2a18] flex items-center justify-center">
              <CompanyIcon name={company.icon} size={18} />
            </div>
            <div>
              <h3 className="font-display font-semibold text-white text-[15px]">{company.name}</h3>
              <div className="text-[11px] font-mono text-[var(--orange)]">{company.ticker} • LIVE ORDER BOOK</div>
            </div>
          </div>
          <button onClick={onClose} className="text-[var(--muted)] hover:text-white p-1"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-3 gap-px bg-[var(--border)] border-b border-[var(--border)]">
          {[['LAST', fmtPrice(company.lastPrice), 'var(--orange)'], ['SPREAD', fmtPrice(company.ask - company.bid), 'var(--text)'], ['MIN LOT', `$${company.minLot.toLocaleString()}`, 'var(--text)']].map(([k, v, c]) => (
            <div key={k} className="bg-[var(--panel)] p-3">
              <div className="text-[9px] font-mono tracking-widest text-[var(--muted-2)]">{k}</div>
              <div className="font-mono text-[15px] mt-1" style={{ color: c }}>{v}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 divide-x divide-[var(--border)]">
          <div>
            <div className="grid grid-cols-3 px-3 py-2 text-[9px] font-mono tracking-widest text-[var(--green)] border-b border-[var(--border)]">
              <span>BID</span><span className="text-right">SIZE</span><span className="text-right">TOTAL</span>
            </div>
            {bids.map((b, i) => <Row key={i} row={b} side="bid" />)}
          </div>
          <div>
            <div className="grid grid-cols-3 px-3 py-2 text-[9px] font-mono tracking-widest text-[var(--red)] border-b border-[var(--border)]">
              <span>ASK</span><span className="text-right">SIZE</span><span className="text-right">TOTAL</span>
            </div>
            {asks.map((a, i) => <Row key={i} row={a} side="ask" />)}
          </div>
        </div>
        <div className="p-3 text-center text-[10px] font-mono text-[var(--muted-2)] border-t border-[var(--border)]">
          SIMULATED DEPTH • NOT AN OFFER TO TRANSACT
        </div>
      </DialogContent>
    </Dialog>
  );
}
